import './Aside.scss';

import { debounce } from '@vaaas/fpts/timing';
import { clamp } from '@vaaas/fpts/maths';
import { sleep, next_frame } from '@vaaas/fpts/promise';
import { defineComponent, h, PropType, VNode } from 'vue';

import QuickIntro from './Aside/QuickIntro';
import Tutorials from './Aside/Tutorials';
import MoreHelp from './Aside/MoreHelp';

import { pause } from '../vimeo';
import Tabs from './Tabs';
import type { State } from '../state';
import { Defined } from '@phi/util/types';
import { px } from '@phi/util/css';

const MAX_WIDTH = 560;
const MIN_WIDTH = 384;
const width_range = clamp(MIN_WIDTH, MAX_WIDTH);

/** The sidebar element, containing documentation and tutorials. */
export default defineComponent({
    props: {
        aside: { required: true, type: null as any as PropType<State['aside']> },
        is_demo: { required: true, type: Boolean },
        tut_stage: { required: true, type: Number },
    },

    emits: {
        'update:aside': (x: State['aside']) => true,
        'update:tut_stage': (x: number) => true,
    },

    methods: {
        pauseVideos() {
            for (const x of (this.$refs.element as HTMLDivElement).querySelectorAll('iframe'))
                pause(x);
        },

        updateWidth: debounce((elem: HTMLDivElement) => {
            const rect = elem.getBoundingClientRect();
            document.documentElement.style.setProperty('--sidebar-width', px(Math.floor(rect.width)));
        }, 100),

        async on_show() {
            if (!this.shown_once) {
                await next_frame();
                this.shown_once = true;
                await next_frame();
            }
            this.width = MAX_WIDTH;
            await sleep(400);
            document.body.classList.add('aside');
        },

        async on_hide() {
            this.pauseVideos();
            document.body.classList.remove('aside');
            this.$emit('update:tut_stage', 100);
            await sleep(400);
        },
    },

    data() {
        return {
            /** indicates that this component has been shown at least once
             *
             * this component begins its lifecycle not being rendered at all.
             * then, when in should be shown, the HTML content is initially rendered.
             * future toggling of this component will only change its visibility, but it will remain in the dom
             *
             * the reason for this handling is that this component contains expensive vimeo resources.
             * we only want to load them if needed, but we don't want to reload them!
             */
            shown_once: false,

            /** the sidebar is draggable. this is its current width. */
            width: MAX_WIDTH,

            /** flag indicating that the cursor should be a resize cursor */
            resize_indicator: false,
        };
    },

    watch: {
        aside(value: typeof this.aside, oldValue: typeof this.aside) {
            if (value && !oldValue) // should appear
                this.on_show();
            else if (oldValue && !value) // should disappear
                this.on_hide();
        },

        width() {
            this.updateWidth(this.$refs.element as HTMLDivElement);
        },
    },

    computed: {
        tabs():  Array<{ id: Defined<State['aside']>; text: string; slot: () => VNode | VNode[] }> {
            const xs = [];
            xs.push({
                id: 'intro' as Defined<State['aside']>,
                text: this.is_demo ? 'Example design' : 'Intro',
                slot: QuickIntro,
            });
            xs.push({
                id: 'tutorials' as Defined<State['aside']>,
                text: 'Tutorials',
                slot: () => h(Tutorials, {
                    is_demo: this.is_demo,
                    stage: this.tut_stage,
                    'onUpdate:stage': (x) => this.$emit('update:tut_stage', x),
                }),
            });
            if (!this.is_demo)
                xs.push({
                    id: 'morehelp' as Defined<State['aside']>,
                    text: 'More help',
                    slot: () => h(MoreHelp),
                });
            return xs;
        },
    },

    mounted() {
        // if for whatever reason the sidebar starts its lifecycle shown, set its state to something sane
        if (this.aside && !this.shown_once)
            requestAnimationFrame(() => this.shown_once = true);
    },

    render() {
        if (!this.aside && !this.shown_once) return '';
        return h('aside', {
            class: {
                'phi-aside': true,
                show: Boolean(this.aside && this.shown_once),
                active: this.resize_indicator
            },
            id: 'aside',
            ref: 'element',
            style: {
                width: px(this.width),
            },
            onMousemove: (e: MouseEvent) => {
                this.resize_indicator = e.x - (this.$refs.element as HTMLDivElement).offsetLeft <= 16;
            },
            onMousedown: (e: MouseEvent) => {
                if (e.x - (this.$refs.element as HTMLDivElement).offsetLeft > 16) return;
                const resize = (e: MouseEvent) => this.width = width_range(this.width - e.movementX);
                document.addEventListener('mousemove', resize);
                document.addEventListener(
                    'mouseup',
                    () => document.removeEventListener('mousemove', resize),
                    { once: true }
                );
            },
        }, h(Tabs, {
            tabs: this.tabs,
            tab: this.aside,
            'onUpdate:tab': (x) => this.$emit('update:aside', x as Defined<State['aside']>),
        }));
    }
});
